import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { Layout, Menu, MenuProps, ConfigProvider, App as AntApp, Spin, Dropdown, Modal, Form, Input, message as MessageApi, Space, Tag, Badge } from 'antd';
import { HomeOutlined, UserOutlined, ShoppingOutlined, BuildOutlined, BoxPlotOutlined, ProductOutlined, LogoutOutlined, FileTextOutlined, DeleteOutlined, SettingOutlined, QrcodeOutlined, KeyOutlined, DownOutlined, ToolOutlined, BookOutlined, SwapOutlined, DollarOutlined, AuditOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import zhCN from 'antd/locale/zh_CN';
import { User, Permission, PermissionType, UserRole } from './types/auth';
import api from './services/api';

const { Header, Content, Sider } = Layout;

interface MenuItemData {
  key: string;
  name: string;
  path: string | null;
  icon: string | null;
  children?: MenuItemData[];
}

const iconMap: Record<string, React.ReactNode> = {
  'HomeOutlined': <HomeOutlined />,
  'UserOutlined': <UserOutlined />,
  'ShoppingOutlined': <ShoppingOutlined />,
  'BuildOutlined': <BuildOutlined />,
  'BoxPlotOutlined': <BoxPlotOutlined />,
  'ProductOutlined': <ProductOutlined />,
  'LogoutOutlined': <LogoutOutlined />,
  'FileTextOutlined': <FileTextOutlined />,
  'DeleteOutlined': <DeleteOutlined />,
  'SettingOutlined': <SettingOutlined />,
  'QrcodeOutlined': <QrcodeOutlined />,
  'ToolOutlined': <ToolOutlined />,
  'BookOutlined': <BookOutlined />,
  'SwapOutlined': <SwapOutlined />,
  'DollarOutlined': <DollarOutlined />,
  'AuditOutlined': <AuditOutlined />,
  'SendOutlined': <SendOutlined />,
  'TeamOutlined': <TeamOutlined />,
};

const getIcon = (iconName: string | null): React.ReactNode => {
  if (!iconName) return null;
  return iconMap[iconName] || null;
};

const MENU_CACHE_KEY = 'menu_cache';
const MENU_CACHE_EXPIRY = 5 * 60 * 1000;

interface MenuCache {
  data: MenuItemData[];
  timestamp: number;
  userId: number;
}

const getCachedMenu = (userId: number): MenuItemData[] | null => {
  try {
    const cached = localStorage.getItem(MENU_CACHE_KEY);
    if (cached) {
      const parsed: MenuCache = JSON.parse(cached);
      if (parsed.userId === userId && Date.now() - parsed.timestamp < MENU_CACHE_EXPIRY) {
        return parsed.data;
      }
    }
  } catch (e) {
    console.warn('读取菜单缓存失败:', e);
  }
  return null;
};

const setCachedMenu = (data: MenuItemData[], userId: number) => {
  try {
    const cache: MenuCache = {
      data,
      timestamp: Date.now(),
      userId
    };
    localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('保存菜单缓存失败:', e);
  }
};

const clearMenuCache = () => {
  localStorage.removeItem(MENU_CACHE_KEY);
};

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 400 }}>
    <Spin size="large" />
  </div>
);

const Home = lazy(() => import('./pages/Home'));
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const ProductManagement = lazy(() => import('./pages/ProductManagement'));
const ProductCategoryManagement = lazy(() => import('./pages/ProductCategoryManagement'));
const ComponentManagement = lazy(() => import('./pages/ComponentManagement'));
const ComponentCategoryManagement = lazy(() => import('./pages/ComponentCategoryManagement'));
const ComponentSpecificationManagement = lazy(() => import('./pages/ComponentSpecificationManagement'));
const ComponentCodeManagement = lazy(() => import('./pages/ComponentCodeManagement'));
const OrderManagement = lazy(() => import('./pages/OrderManagement'));
const OrderStatusManagement = lazy(() => import('./pages/OrderStatusManagement'));
const ProductionPlanning = lazy(() => import('./pages/ProductionPlanning'));
const ComponentInventoryManagement = lazy(() => import('./pages/ComponentInventoryManagement'));
const WarehouseManagement = lazy(() => import('./pages/WarehouseManagement'));
const ShipmentStatistics = lazy(() => import('./pages/ShipmentStatistics'));
const ReturnOrderManagement = lazy(() => import('./pages/ReturnOrderManagement'));
const StocktakingManagement = lazy(() => import('./pages/StocktakingManagement'));
const TransferOrderManagement = lazy(() => import('./pages/TransferOrderManagement'));
const Login = lazy(() => import('./pages/Login'));
const EnterpriseSettings = lazy(() => import('./pages/EnterpriseSettings'));
const EnterpriseInfoManagement = lazy(() => import('./pages/EnterpriseInfoManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const OperationLogManagement = lazy(() => import('./pages/OperationLogManagement'));
const RecycleBinManagement = lazy(() => import('./pages/RecycleBinManagement'));
const SystemMonitoring = lazy(() => import('./pages/SystemMonitoring'));
const SystemSetting = lazy(() => import('./pages/SystemSetting'));
const SystemUpdate = lazy(() => import('./pages/SystemUpdate'));
const PrintPage = lazy(() => import('./pages/PrintPage'));
const ProductionOrderPrint = lazy(() => import('./pages/ProductionOrderPrint'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const JobTypeManagement = lazy(() => import('./pages/JobTypeManagement'));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement'));
const WageStandardManagement = lazy(() => import('./pages/WageStandardManagement'));
const PieceWageRateManagement = lazy(() => import('./pages/PieceWageRateManagement'));
const InventoryLogManagement = lazy(() => import('./pages/InventoryLogManagement'));
const EmployeeWorkManagement = lazy(() => import('./pages/EmployeeWorkManagement'));
const FinancialOverview = lazy(() => import('./pages/FinancialOverview'));
const IncomeStatistics = lazy(() => import('./pages/IncomeStatistics'));
const ExpenseCategoryManagement = lazy(() => import('./pages/ExpenseCategoryManagement'));
const ExpenseManagement = lazy(() => import('./pages/ExpenseManagement'));
const SalarySlipManagement = lazy(() => import('./pages/SalarySlipManagement'));
const FinancialAccountManagement = lazy(() => import('./pages/FinancialAccountManagement'));
const FinancialReport = lazy(() => import('./pages/FinancialReport'));
const ReceivablesManagement = lazy(() => import('./pages/ReceivablesManagement'));
const AuditManagement = lazy(() => import('./pages/AuditManagement'));
const MenuManagement = lazy(() => import('./pages/MenuManagement'));

const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  return !!token && !!user;
};

const getUserInfo = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    return JSON.parse(userStr);
  }
  return null;
};

const hasPermission = (user: User | null, code: string): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!user.permissions || !Array.isArray(user.permissions)) return false;
  return user.permissions.some((p: Permission) => {
    const hasCode = p.code === code;
    const isWebType = p.type === PermissionType.WEB || p.type === 'web';
    return hasCode && isWebType;
  });
};

const canAccessWeb = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  
  if (user.roles && user.roles.length > 0) {
    if (user.roles.some(role => role.isActive && role.canAccessWeb)) {
      return true;
    }
  }
  
  if (user.permissions && user.permissions.length > 0) {
    const hasWebPermission = user.permissions.some((p: Permission) => {
      return p.type === PermissionType.WEB || p.type === 'web';
    });
    if (hasWebPermission) {
      return true;
    }
  }
  
  return false;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissionCode?: string | string[];
  user: User | null;
  onPermissionDenied: () => void;
  requireSuperAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, permissionCode, user, onPermissionDenied, requireSuperAdmin }) => {
  const checkPermission = useCallback(() => {
    if (!permissionCode) return true;
    if (Array.isArray(permissionCode)) {
      return permissionCode.some(code => hasPermission(user, code));
    }
    return hasPermission(user, permissionCode);
  }, [permissionCode, user]);

  useEffect(() => {
    if (!isAuthenticated()) {
      onPermissionDenied();
    } else if (!canAccessWeb(user)) {
      onPermissionDenied();
    } else if (!checkPermission()) {
      onPermissionDenied();
    } else if (requireSuperAdmin && user?.isSuperAdmin !== true) {
      onPermissionDenied();
    }
  }, [permissionCode, user, onPermissionDenied, requireSuperAdmin, checkPermission]);
  
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  
  if (!canAccessWeb(user)) {
    return <Navigate to="/no-access" replace />;
  }
  
  if (!checkPermission()) {
    return <Navigate to="/no-permission" replace />;
  }
  
  if (requireSuperAdmin && user?.isSuperAdmin !== true) {
    return <Navigate to="/no-permission" replace />;
  }
  
  return <>{children}</>;
};

const NoAccess: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
    <h1>无访问权限</h1>
    <p>您没有访问网站的权限，请联系管理员。</p>
  </div>
);

const NoPermission: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
    <h1>权限不足</h1>
    <p>您没有访问此页面的权限，请联系管理员。</p>
  </div>
);

const convertMenuDataToItems = (menuData: MenuItemData[]): MenuProps['items'] => {
  return menuData.map(item => {
    const menuItem: any = {
      key: item.key,
      label: item.name,
    };
    
    if (item.icon) {
      menuItem.icon = getIcon(item.icon);
    }
    
    if (item.children && item.children.length > 0) {
      menuItem.children = convertMenuDataToItems(item.children);
    }
    
    return menuItem;
  });
};

const buildMenuItems = (user: User | null, pendingAuditCount: number = 0): MenuProps['items'] => {
  if (!user) return [];
  
  const isAdmin = user.role === 'admin';
  const items: MenuProps['items'] = [];

  if (hasPermission(user, 'home')) {
    items.push({
      key: '1',
      icon: <HomeOutlined />,
      label: '首页'
    });
  }

  if (hasPermission(user, 'user_guide')) {
    items.push({
      key: '1-0',
      icon: <BookOutlined />,
      label: '使用指引'
    });
  }

  if (hasPermission(user, 'customers')) {
    items.push({
      key: '2',
      icon: <UserOutlined />,
      label: '客户管理'
    });
  }

  const productChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'products')) {
    productChildren.push({
      key: '3-0',
      label: '产品列表'
    });
  }
  if (hasPermission(user, 'product_categories')) {
    productChildren.push({
      key: '3-2',
      label: '产品分类'
    });
  }
  if (productChildren.length > 0) {
    items.push({
      key: '3',
      icon: <ProductOutlined />,
      label: '产品管理',
      children: productChildren
    });
  }

  const componentChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'components')) {
    componentChildren.push({
      key: '3-1-0',
      label: '组件列表'
    });
  }
  if (hasPermission(user, 'component_categories')) {
    componentChildren.push({
      key: '3-1-1',
      label: '组件分类管理'
    });
  }
  if (hasPermission(user, 'component_specifications')) {
    componentChildren.push({
      key: '3-1-2',
      label: '组件规格管理'
    });
  }
  if (componentChildren.length > 0) {
    items.push({
      key: '3-1',
      icon: <BuildOutlined />,
      label: '组件管理',
      children: componentChildren
    });
  }

  const orderChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'orders')) {
    orderChildren.push({
      key: '4-0',
      label: '订单列表'
    });
  }

  if (hasPermission(user, 'order_status')) {
    orderChildren.push({
      key: '4-2',
      label: '订单状态管理'
    });
  }
  if (orderChildren.length > 0) {
    items.push({
      key: '4',
      icon: <ShoppingOutlined />,
      label: '订单管理',
      children: orderChildren
    });
  }

  if (hasPermission(user, 'production') || hasPermission(user, 'picking')) {
    items.push({
      key: '5',
      icon: <BuildOutlined />,
      label: '配货及生产'
    });
  }

  const inventoryChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'component_inventory')) {
    inventoryChildren.push({
      key: '6-1',
      label: '组件库存'
    });
  }
  if (hasPermission(user, 'warehouse')) {
    inventoryChildren.push({
      key: '6-2',
      label: '仓库管理'
    });
  }
  if (hasPermission(user, 'inventory_logs')) {
    inventoryChildren.push({
      key: '6-4',
      label: '库存明细'
    });
  }
  if (inventoryChildren.length > 0) {
    items.push({
      key: '6',
      icon: <BoxPlotOutlined />,
      label: '库存管理',
      children: inventoryChildren
    });
  }

  if (hasPermission(user, 'component_codes')) {
    items.push({
      key: '6-3',
      icon: <QrcodeOutlined />,
      label: '组件码管理'
    });
  }

  const stockChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'return_orders')) {
    stockChildren.push({
      key: '12-0',
      label: '退货管理'
    });
  }
  if (hasPermission(user, 'stocktakings')) {
    stockChildren.push({
      key: '12-1',
      label: '盘点管理'
    });
  }
  if (hasPermission(user, 'transfer_orders')) {
    stockChildren.push({
      key: '12-2',
      label: '调拨管理'
    });
  }
  if (stockChildren.length > 0) {
    items.push({
      key: '12',
      icon: <SwapOutlined />,
      label: '库存作业',
      children: stockChildren
    });
  }

  const settingsChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'users')) {
    settingsChildren.push({
      key: '8-0',
      label: '用户管理'
    });
  }
  if (isAdmin) {
    settingsChildren.push({
      key: '8-2',
      label: '角色管理'
    });
  }
  if (hasPermission(user, 'enterprise_info')) {
    settingsChildren.push({
      key: '8-1',
      label: '企业信息'
    });
  }
  if (settingsChildren.length > 0) {
    items.push({
      key: '8',
      icon: <SettingOutlined />,
      label: '企业设置',
      children: settingsChildren
    });
  }

  const employeeChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'job_types')) {
    employeeChildren.push({
      key: '13-0',
      label: '工种管理'
    });
  }
  if (hasPermission(user, 'employees')) {
    employeeChildren.push({
      key: '13-1',
      label: '员工管理'
    });
  }
  if (hasPermission(user, 'wage_standards')) {
    employeeChildren.push({
      key: '13-2',
      label: '工资标准'
    });
  }
  if (hasPermission(user, 'piece_wage_rates')) {
    employeeChildren.push({
      key: '13-3',
      label: '计件单价'
    });
  }
  if (hasPermission(user, 'employee_work')) {
    employeeChildren.push({
      key: '13-4',
      label: '员工工作量'
    });
  }
  if (employeeChildren.length > 0) {
    items.push({
      key: '13',
      icon: <UserOutlined />,
      label: '员工管理',
      children: employeeChildren
    });
  }

  const accountingChildren: MenuProps['items'] = [];
  if (hasPermission(user, 'accounting_overview')) {
    accountingChildren.push({
      key: '14-0',
      label: '财务概览'
    });
  }
  if (hasPermission(user, 'accounting_income')) {
    accountingChildren.push({
      key: '14-1',
      label: '收入统计'
    });
  }
  if (hasPermission(user, 'accounting_expense')) {
    accountingChildren.push({
      key: '14-2',
      label: '支出记录'
    });
  }
  if (hasPermission(user, 'accounting_expense_category')) {
    accountingChildren.push({
      key: '14-3',
      label: '支出分类'
    });
  }
  if (hasPermission(user, 'accounting_salary')) {
    accountingChildren.push({
      key: '14-4',
      label: '工资管理'
    });
  }
  if (hasPermission(user, 'accounting_account')) {
    accountingChildren.push({
      key: '14-5',
      label: '账户管理'
    });
  }
  if (hasPermission(user, 'accounting_report')) {
    accountingChildren.push({
      key: '14-6',
      label: '财务报表'
    });
  }
  if (hasPermission(user, 'accounting_report')) {
    accountingChildren.push({
      key: '14-7',
      label: '应收账款'
    });
  }
  if (hasPermission(user, 'shipments')) {
    accountingChildren.push({
      key: '14-8',
      label: '发货统计'
    });
  }
  if (accountingChildren.length > 0) {
    items.push({
      key: '14',
      icon: <DollarOutlined />,
      label: '会计对账',
      children: accountingChildren
    });
  }

  if (hasPermission(user, 'audit_view')) {
    items.push({
      key: '15',
      icon: pendingAuditCount > 0 ? (
        <Badge count={pendingAuditCount} size="small" offset={[8, 0]}>
          <AuditOutlined />
        </Badge>
      ) : <AuditOutlined />,
      label: '审核专区'
    });
  }

  if (user?.isSuperAdmin === true) {
    items.push({
      key: '9',
      icon: <ToolOutlined />,
      label: '系统设置',
      children: [
        { key: '9-1', label: '系统维护' },
        { key: '9-2', label: '系统监控' },
        { key: '9-3', label: '有效期设置' },
        { key: '9-4', label: '系统更新' },
        { key: '9-5', label: '菜单管理' }
      ]
    });
  }

  if (hasPermission(user, 'operation_logs')) {
    items.push({
      key: '10',
      icon: <FileTextOutlined />,
      label: '操作日志'
    });
  }

  if (hasPermission(user, 'recycle_bin')) {
    items.push({
      key: '11',
      icon: <DeleteOutlined />,
      label: '回收站'
    });
  }

  return items;
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [permissionVersion, setPermissionVersion] = useState(0);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [messageApi] = MessageApi.useMessage();
  const [menuData, setMenuData] = useState<MenuItemData[]>([]);
  const [pendingAuditCount, setPendingAuditCount] = useState(0);

  const isPrintPage = location.pathname.startsWith('/print') || location.pathname.startsWith('/production-print');

  const fetchPendingAuditCount = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      const data = await api.get('/audit-records/statistics') as { pending: number };
      setPendingAuditCount(data?.pending || 0);
    } catch (error) {
      console.error('获取待审核数量失败:', error);
    }
  }, []);

  const fetchMenuData = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated()) return;
    
    const currentUser = getUserInfo();
    if (!currentUser) return;
    
    if (!forceRefresh) {
      const cached = getCachedMenu(currentUser.id);
      if (cached && cached.length > 0) {
        setMenuData(cached);
        return;
      }
    }
    
    try {
      const data = await api.get('/menu-items/tree') as MenuItemData[];
      if (data && data.length > 0) {
        setMenuData(data);
        setCachedMenu(data, currentUser.id);
      }
    } catch (error: any) {
      console.error('获取菜单失败:', error);
    }
  }, []);

  const refreshUserPermissions = useCallback(async () => {
    if (!isAuthenticated()) return;
    
    try {
      const data = await api.get('/auth/my-permissions') as {
        canAccessWeb: boolean;
        canAccessMiniprogram: boolean;
        permissions: Permission[];
        role: string;
        roles: any[];
        isSuperAdmin?: boolean;
      };
      
      const currentUser = getUserInfo();
      if (currentUser) {
        const oldPermIds = (currentUser.permissions || []).map(p => p.id).sort().join(',');
        const newPermIds = (data.permissions || []).map(p => p.id).sort().join(',');
        
        const permissionsChanged = oldPermIds !== newPermIds;
        
        const updatedUser: User = {
          ...currentUser,
          permissions: data.permissions || [],
          role: data.role as any,
          roles: data.roles || currentUser.roles || [],
          isSuperAdmin: data.isSuperAdmin ?? currentUser.isSuperAdmin,
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        
        if (permissionsChanged) {
          setPermissionVersion(v => v + 1);
          console.log('权限已更新');
        }
      }
    } catch (error: any) {
      if (error?.code === 'ECONNREFUSED' || error?.message?.includes('Network Error')) {
        console.warn('后端服务未启动，跳过权限刷新');
        return;
      }
      console.error('刷新权限失败:', error);
    }
  }, []);

  useEffect(() => {
    const userInfo = getUserInfo();
    setUser(userInfo);
    setLoading(false);
    
    if (isAuthenticated()) {
      fetchMenuData();
      fetchPendingAuditCount();
      setTimeout(() => {
        refreshUserPermissions();
      }, 2000);
      
      const interval = setInterval(refreshUserPermissions, 30000);
      const auditInterval = setInterval(fetchPendingAuditCount, 60000);
      
      return () => {
        clearInterval(interval);
        clearInterval(auditInterval);
      };
    }
  }, [refreshUserPermissions, fetchMenuData, fetchPendingAuditCount]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'token') {
        const userInfo = getUserInfo();
        setUser(userInfo);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const contentElement = document.querySelector('.ant-layout-content');
    if (contentElement) {
      contentElement.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleMenuUpdate = () => {
      clearMenuCache();
      fetchMenuData(true);
    };
    
    window.addEventListener('menuUpdated', handleMenuUpdate);
    return () => window.removeEventListener('menuUpdated', handleMenuUpdate);
  }, [fetchMenuData]);

  const handlePermissionDenied = useCallback(() => {
    refreshUserPermissions();
  }, [refreshUserPermissions]);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    refreshUserPermissions();
    
    const { key } = e;
    switch (key) {
      case '1':
        navigate('/');
        break;
      case '1-0':
        navigate('/user-guide');
        break;
      case '2':
        navigate('/customers');
        break;
      case '3':
      case '3-0':
        navigate('/products');
        break;
      case '3-1':
      case '3-1-0':
        navigate('/components');
        break;
      case '3-1-1':
        navigate('/component-categories');
        break;
      case '3-1-2':
        navigate('/component-specifications');
        break;
      case '3-2':
        navigate('/product-categories');
        break;
      case '4':
      case '4-0':
        navigate('/orders');
        break;
      case '4-1':
        navigate('/picking');
        break;
      case '4-2':
        navigate('/order-status');
        break;
      case '5':
        navigate('/production');
        break;
      case '6':
      case '6-1':
        navigate('/component-inventory');
        break;
      case '6-2':
        navigate('/warehouse-management');
        break;
      case '6-3':
        navigate('/component-codes');
        break;
      case '6-4':
        navigate('/inventory-logs');
        break;
      case '14-8':
        navigate('/shipments');
        break;
      case '15':
        navigate('/audit');
        break;
      case '9-1':
        navigate('/system-maintenance');
        break;
      case '9-2':
        navigate('/system-monitoring');
        break;
      case '9-3':
        navigate('/validity-settings');
        break;
      case '9-4':
        navigate('/system-update');
        break;
      case '9-5':
        navigate('/menu-management');
        break;
      case '12-0':
        navigate('/return-orders');
        break;
      case '12-1':
        navigate('/stocktakings');
        break;
      case '12-2':
        navigate('/transfer-orders');
        break;
      case '8':
        navigate('/users');
        break;
      case '8-0':
        navigate('/users');
        break;
      case '8-1':
        navigate('/enterprise-info');
        break;
      case '8-2':
        navigate('/roles');
        break;
      case '13-0':
        navigate('/job-types');
        break;
      case '13-1':
        navigate('/employees');
        break;
      case '13-2':
        navigate('/wage-standards');
        break;
      case '13-3':
        navigate('/piece-wage-rates');
        break;
      case '13-4':
        navigate('/employee-work');
        break;
      case '14':
      case '14-0':
        navigate('/financial-overview');
        break;
      case '14-1':
        navigate('/income-statistics');
        break;
      case '14-2':
        navigate('/expenses');
        break;
      case '14-3':
        navigate('/expense-categories');
        break;
      case '14-4':
        navigate('/salary-slips');
        break;
      case '14-5':
        navigate('/financial-accounts');
        break;
      case '14-6':
        navigate('/financial-reports');
        break;
      case '14-7':
        navigate('/receivables');
        break;
      case '10':
        navigate('/operation-logs');
        break;
      case '11':
        navigate('/recycle-bin');
        break;
      case 'logout':
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearMenuCache();
        window.location.reload();
        break;
      default:
        break;
    }
  };

  const handleChangePassword = () => {
    setIsPasswordModalVisible(true);
  };

  const handlePasswordModalOk = async () => {
    try {
      const values = await passwordForm.validateFields();
      
      if (values.newPassword !== values.confirmPassword) {
        setTimeout(() => messageApi.error('两次输入的密码不一致'), 0);
        return;
      }
      
      await api.put(`/users/${user?.id}/password`, {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword
      });
      
      setTimeout(() => messageApi.success('密码修改成功'), 0);
      setIsPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error: any) {
      setTimeout(() => messageApi.error(error.response?.data?.message || '密码修改失败'), 0);
    }
  };

  const handlePasswordModalCancel = () => {
    setIsPasswordModalVisible(false);
    passwordForm.resetFields();
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'password',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: handleChangePassword
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
      }
    }
  ];

  const menuItems = useMemo(() => {
    let items: MenuProps['items'];
    
    if (menuData && menuData.length > 0) {
      items = convertMenuDataToItems(menuData);
    } else {
      items = buildMenuItems(user, pendingAuditCount);
    }
    
    if (items && user) {
      items = [...items, {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '登出'
      }];
    }
    
    return items;
  }, [menuData, user, permissionVersion, pendingAuditCount]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isAuthenticated() && user) {
    if (isPrintPage) {
      return (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/print/:orderId" element={<PrintPage />} />
            <Route path="/print" element={<PrintPage />} />
            <Route path="/production-print" element={<ProductionOrderPrint />} />
          </Routes>
        </Suspense>
      );
    }
    
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sider 
          width={200} 
          style={{ 
            background: '#fff',
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0
          }}
        >
          <Menu
            mode="inline"
            defaultSelectedKeys={['1']}
            style={{ height: '100%', borderRight: 0 }}
            onClick={handleMenuClick}
            items={menuItems}
          />
        </Sider>
        <Layout style={{ marginLeft: 200 }}>
          <Header style={{ 
            background: '#fff', 
            padding: '0 24px', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
          }}>
            <Space size="large">
              <Tag color={user.role === UserRole.ADMIN ? 'red' : 'blue'}>
                {user.role === UserRole.ADMIN ? '管理员' : '普通用户'}
              </Tag>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Space style={{ cursor: 'pointer' }}>
                  <UserOutlined style={{ fontSize: 16 }} />
                  <span style={{ fontWeight: 500 }}>{user.username}</span>
                  <DownOutlined style={{ fontSize: 12 }} />
                </Space>
              </Dropdown>
            </Space>
          </Header>
          <Content style={{ 
            margin: '16px', 
            overflow: 'auto',
            height: 'calc(100vh - 64px - 32px)'
          }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/no-access" element={<NoAccess />} />
                <Route path="/no-permission" element={<NoPermission />} />
                
                <Route path="/" element={<ProtectedRoute permissionCode="home" user={user} onPermissionDenied={handlePermissionDenied}><Home /></ProtectedRoute>} />
                <Route path="/user-guide" element={<ProtectedRoute permissionCode="user_guide" user={user} onPermissionDenied={handlePermissionDenied}><UserGuide /></ProtectedRoute>} />
                <Route path="/customers" element={<ProtectedRoute permissionCode="customers" user={user} onPermissionDenied={handlePermissionDenied}><CustomerManagement /></ProtectedRoute>} />
                <Route path="/products" element={<ProtectedRoute permissionCode="products" user={user} onPermissionDenied={handlePermissionDenied}><ProductManagement /></ProtectedRoute>} />
                <Route path="/product-categories" element={<ProtectedRoute permissionCode="product_categories" user={user} onPermissionDenied={handlePermissionDenied}><ProductCategoryManagement /></ProtectedRoute>} />
                <Route path="/components" element={<ProtectedRoute permissionCode="components" user={user} onPermissionDenied={handlePermissionDenied}><ComponentManagement /></ProtectedRoute>} />
                <Route path="/component-categories" element={<ProtectedRoute permissionCode="component_categories" user={user} onPermissionDenied={handlePermissionDenied}><ComponentCategoryManagement /></ProtectedRoute>} />
                <Route path="/component-specifications" element={<ProtectedRoute permissionCode="component_specifications" user={user} onPermissionDenied={handlePermissionDenied}><ComponentSpecificationManagement /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute permissionCode="orders" user={user} onPermissionDenied={handlePermissionDenied}><OrderManagement /></ProtectedRoute>} />
                <Route path="/order-status" element={<ProtectedRoute permissionCode="order_status" user={user} onPermissionDenied={handlePermissionDenied}><OrderStatusManagement /></ProtectedRoute>} />
                <Route path="/production" element={<ProtectedRoute permissionCode={['picking', 'production']} user={user} onPermissionDenied={handlePermissionDenied}><ProductionPlanning /></ProtectedRoute>} />
                <Route path="/component-inventory" element={<ProtectedRoute permissionCode="component_inventory" user={user} onPermissionDenied={handlePermissionDenied}><ComponentInventoryManagement /></ProtectedRoute>} />
                <Route path="/warehouse-management" element={<ProtectedRoute permissionCode="warehouse" user={user} onPermissionDenied={handlePermissionDenied}><WarehouseManagement /></ProtectedRoute>} />
                <Route path="/component-codes" element={<ProtectedRoute permissionCode="component_codes" user={user} onPermissionDenied={handlePermissionDenied}><ComponentCodeManagement /></ProtectedRoute>} />
                <Route path="/inventory-logs" element={<ProtectedRoute permissionCode="inventory_logs" user={user} onPermissionDenied={handlePermissionDenied}><InventoryLogManagement /></ProtectedRoute>} />
                <Route path="/shipments" element={<ProtectedRoute permissionCode="shipments" user={user} onPermissionDenied={handlePermissionDenied}><ShipmentStatistics /></ProtectedRoute>} />
                <Route path="/return-orders" element={<ProtectedRoute permissionCode="return_orders" user={user} onPermissionDenied={handlePermissionDenied}><ReturnOrderManagement /></ProtectedRoute>} />
                <Route path="/stocktakings" element={<ProtectedRoute permissionCode="stocktakings" user={user} onPermissionDenied={handlePermissionDenied}><StocktakingManagement /></ProtectedRoute>} />
                <Route path="/transfer-orders" element={<ProtectedRoute permissionCode="transfer_orders" user={user} onPermissionDenied={handlePermissionDenied}><TransferOrderManagement /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute permissionCode="users" user={user} onPermissionDenied={handlePermissionDenied}><UserManagement /></ProtectedRoute>} />
                <Route path="/roles" element={<ProtectedRoute user={user} onPermissionDenied={handlePermissionDenied}><RoleManagement /></ProtectedRoute>} />
                <Route path="/job-types" element={<ProtectedRoute permissionCode="job_types" user={user} onPermissionDenied={handlePermissionDenied}><JobTypeManagement /></ProtectedRoute>} />
                <Route path="/employees" element={<ProtectedRoute permissionCode="employees" user={user} onPermissionDenied={handlePermissionDenied}><EmployeeManagement /></ProtectedRoute>} />
                <Route path="/wage-standards" element={<ProtectedRoute permissionCode="wage_standards" user={user} onPermissionDenied={handlePermissionDenied}><WageStandardManagement /></ProtectedRoute>} />
                <Route path="/piece-wage-rates" element={<ProtectedRoute permissionCode="piece_wage_rates" user={user} onPermissionDenied={handlePermissionDenied}><PieceWageRateManagement /></ProtectedRoute>} />
                <Route path="/employee-work" element={<ProtectedRoute permissionCode="employee_work" user={user} onPermissionDenied={handlePermissionDenied}><EmployeeWorkManagement /></ProtectedRoute>} />
                <Route path="/financial-overview" element={<ProtectedRoute permissionCode="accounting_overview" user={user} onPermissionDenied={handlePermissionDenied}><FinancialOverview /></ProtectedRoute>} />
                <Route path="/income-statistics" element={<ProtectedRoute permissionCode="accounting_income" user={user} onPermissionDenied={handlePermissionDenied}><IncomeStatistics /></ProtectedRoute>} />
                <Route path="/expenses" element={<ProtectedRoute permissionCode="accounting_expense" user={user} onPermissionDenied={handlePermissionDenied}><ExpenseManagement /></ProtectedRoute>} />
                <Route path="/expense-categories" element={<ProtectedRoute permissionCode="accounting_expense_category" user={user} onPermissionDenied={handlePermissionDenied}><ExpenseCategoryManagement /></ProtectedRoute>} />
                <Route path="/salary-slips" element={<ProtectedRoute permissionCode="accounting_salary" user={user} onPermissionDenied={handlePermissionDenied}><SalarySlipManagement /></ProtectedRoute>} />
                <Route path="/financial-accounts" element={<ProtectedRoute permissionCode="accounting_account" user={user} onPermissionDenied={handlePermissionDenied}><FinancialAccountManagement /></ProtectedRoute>} />
                <Route path="/financial-reports" element={<ProtectedRoute permissionCode="accounting_report" user={user} onPermissionDenied={handlePermissionDenied}><FinancialReport /></ProtectedRoute>} />
                <Route path="/receivables" element={<ProtectedRoute permissionCode="accounting_report" user={user} onPermissionDenied={handlePermissionDenied}><ReceivablesManagement /></ProtectedRoute>} />
                <Route path="/audit" element={<ProtectedRoute permissionCode="audit_view" user={user} onPermissionDenied={handlePermissionDenied}><AuditManagement /></ProtectedRoute>} />
                <Route path="/validity-settings" element={<ProtectedRoute user={user} onPermissionDenied={handlePermissionDenied} requireSuperAdmin><SystemSetting /></ProtectedRoute>} />
                <Route path="/menu-management" element={<ProtectedRoute permissionCode="menu_management" user={user} onPermissionDenied={handlePermissionDenied}><MenuManagement /></ProtectedRoute>} />
                <Route path="/enterprise-info" element={<ProtectedRoute permissionCode="enterprise_info" user={user} onPermissionDenied={handlePermissionDenied}><EnterpriseInfoManagement /></ProtectedRoute>} />
                <Route path="/system-maintenance" element={<ProtectedRoute user={user} onPermissionDenied={handlePermissionDenied} requireSuperAdmin><EnterpriseSettings /></ProtectedRoute>} />
                <Route path="/system-monitoring" element={<ProtectedRoute user={user} onPermissionDenied={handlePermissionDenied} requireSuperAdmin><SystemMonitoring /></ProtectedRoute>} />
                <Route path="/system-setting" element={<ProtectedRoute user={user} onPermissionDenied={handlePermissionDenied} requireSuperAdmin><SystemSetting /></ProtectedRoute>} />
                <Route path="/system-update" element={<ProtectedRoute user={user} onPermissionDenied={handlePermissionDenied} requireSuperAdmin><SystemUpdate /></ProtectedRoute>} />
                <Route path="/operation-logs" element={<ProtectedRoute permissionCode="operation_logs" user={user} onPermissionDenied={handlePermissionDenied}><OperationLogManagement /></ProtectedRoute>} />
                <Route path="/recycle-bin" element={<ProtectedRoute permissionCode="recycle_bin" user={user} onPermissionDenied={handlePermissionDenied}><RecycleBinManagement /></ProtectedRoute>} />
                
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </Content>
        </Layout>
        
        <Modal
          title="修改密码"
          open={isPasswordModalVisible}
          onOk={handlePasswordModalOk}
          onCancel={handlePasswordModalCancel}
          okText="确认"
          cancelText="取消"
        >
          <Form form={passwordForm} layout="vertical">
            <Form.Item 
              name="oldPassword" 
              label="原密码" 
              rules={[{ required: true, message: '请输入原密码' }]}
            >
              <Input.Password placeholder="请输入原密码" />
            </Form.Item>
            <Form.Item 
              name="newPassword" 
              label="新密码" 
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码长度不能少于6位' }
              ]}
            >
              <Input.Password placeholder="请输入新密码" />
            </Form.Item>
            <Form.Item 
              name="confirmPassword" 
              label="确认新密码" 
              rules={[
                { required: true, message: '请确认新密码' },
                { min: 6, message: '密码长度不能少于6位' }
              ]}
            >
              <Input.Password placeholder="请再次输入新密码" />
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  dayjs.locale('zh-cn');

  return (
    <ConfigProvider 
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#1890ff',
        },
      }}
    >
      <AntApp>
        <Router>
          <AppContent />
        </Router>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
